<script setup>
import { computed, toRef } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import TaskStatusSelect from 'dashboard/components-next/Tasks/TaskStatusSelect.vue';
import { useTaskDueDate } from 'dashboard/composables/useTaskDueDate';

const props = defineProps({
  task: { type: Object, required: true },
});

const emit = defineEmits(['statusChange', 'delete']);

const { t } = useI18n();
const { label: dueLabel, isOverdue } = useTaskDueDate(toRef(props, 'task'));

const isCompleted = computed(() => props.task.status === 'done');

// An unassigned task still belongs to whoever created it — see
// TaskTable.vue for the same fallback.
const owner = computed(() => props.task.assignee || props.task.createdBy);
const isImplicitOwner = computed(
  () => !props.task.assignee && !!props.task.createdBy
);

const handleStatusChange = status => emit('statusChange', status);
const handleDelete = () => emit('delete');
</script>

<template>
  <div class="flex flex-col gap-2 border-b border-n-weak group/task">
    <div class="flex items-start justify-between gap-2">
      <span
        class="text-sm leading-snug whitespace-pre-line line-clamp-3"
        :class="
          isCompleted
            ? 'line-through text-n-slate-10'
            : 'text-n-slate-12 font-medium'
        "
      >
        {{ task.description }}
      </span>
      <Button
        variant="ghost"
        color="ruby"
        size="xs"
        icon="i-lucide-trash"
        class="opacity-0 group-hover/task:opacity-100 flex-shrink-0"
        @click="handleDelete"
      />
    </div>
    <div class="flex flex-wrap items-center gap-2">
      <TaskStatusSelect
        :model-value="task.status"
        @update:model-value="handleStatusChange"
      />
      <span
        v-if="dueLabel"
        class="inline-flex items-center gap-1 text-xs rounded-full px-1.5 py-0.5"
        :class="
          isOverdue
            ? 'bg-n-ruby-9/10 text-n-ruby-11 font-medium'
            : 'text-n-slate-11'
        "
      >
        <Icon
          :icon="isOverdue ? 'i-lucide-alarm-clock' : 'i-lucide-calendar'"
          size="size-3"
        />
        {{ dueLabel }}
      </span>
      <span
        v-if="owner"
        class="inline-flex items-center gap-1 text-xs text-n-slate-11"
      >
        <Avatar
          :name="owner.name"
          :src="owner.thumbnail"
          :size="16"
          rounded-full
        />
        {{ owner.name }}
        <span v-if="isImplicitOwner">
          · {{ t('TASKS_DASHBOARD.CREATED_BY_SUFFIX') }}
        </span>
      </span>
    </div>
  </div>
</template>
